import {DateTime} from "luxon";
import {BASE_MONTHS} from "../../../../helpers/months";
import type {EBirdMyDataSchema} from "ebird-mydata-reader";
import {MONTHLY_SIGHTINGS_MIN_BAR_HEIGHT} from "./constants";
import type {MonthlySightingsChartData, MonthlySightingsMonth} from "./types";

function getObservationMonth(observation: EBirdMyDataSchema) {
    const observationDate = DateTime.fromISO(observation.date);
    return observationDate.isValid ? observationDate.month : undefined;
}

function getPeakMonthsLabel(months: MonthlySightingsMonth[]) {
    const peakMonths = months.filter(month => month.isPeak);

    if (!peakMonths.length) {
        return '';
    }

    return peakMonths
        .map(month => month.label)
        .join(' - ');
}

export function getMonthlySightingsChartData(observations: EBirdMyDataSchema[]): MonthlySightingsChartData {
    const monthlyCounts = new Map<number, number>();

    for (let month = 1; month <= 12; month++) {
        monthlyCounts.set(month, 0);
    }

    for (const observation of observations) {
        const month = getObservationMonth(observation);
        if (!month) continue;

        monthlyCounts.set(month, (monthlyCounts.get(month) ?? 0) + 1);
    }

    const peakCount = Math.max(0, ...Array.from(monthlyCounts.values()));
    const months = BASE_MONTHS.map(({value, label}) => {
        const count = monthlyCounts.get(value) ?? 0;
        const heightPercent = peakCount && count > 0
            ? Math.max(MONTHLY_SIGHTINGS_MIN_BAR_HEIGHT, Math.round((count / peakCount) * 100))
            : 0;

        return {
            month: value,
            label,
            count,
            heightPercent,
            hasSightings: count > 0,
            isPeak: peakCount > 0 && count === peakCount
        };
    });

    return {
        months,
        peakMonthsLabel: getPeakMonthsLabel(months),
        peakCount
    };
}
